import React, { useMemo, useState, useRef } from 'react';
import { useLocaleContext } from '../../../../../context/LocaleContext';
import { useScheduleContext } from '../../../../../context/ScheduleContext';
import { useModalContext } from '../../../../../context/ModalContext';
import { useWidgetContext } from '../../../../../context/WidgetContextProvider';
import { motion } from "framer-motion";
import { unixFormat } from '../../../../../helpers/times';
import { hexToRgba } from '../../../../../helpers/hexToRgb';
import { episodeMembers } from '../../../../../helpers/schedule-helper';
import { PAGES_VIEW_LINES } from '../../../../../constants/page-settings/schedule';
import ScheduleEpisodeStatus from '../../../ScheduleComponents/ScheduleEpisodeStatus';
import ScheduleUserCategory from '../../../ScheduleComponents/ScheduleUserCategory';
import ScheduleFullness from '../../../ScheduleComponents/ScheduleFullness';
import ScheduleHeaderActions from '../../../ScheduleComponents/ScheduleHeaderActions';
import ScheduleMemberFullInfo from '../../../ScheduleComponents/ScheduleMembers/ScheduleMemberFullInfo';
import MembersSimpleView from '../../../../Members/MembersComponents/MembersSimpleView';
import PartnersBaseView from '../../../../Partners/PartnersViews/PartnersBaseView';
import MemberDetailsModal from '../../../../Members/MembersComponents/MemberDetailsModal';
import { CButton, CTags } from '../../../../UI';
import EpisodeRecordStatus from '../../../ScheduleComponents/EpisodeRecordStatus';
import { useApplicationContext } from '../../../../../context/ApplicationContext';
import { useEventContext } from '../../../../../context/EventContext';
import storage from '../../../../../../../player/js/storage';
import Icons from '../../../../Icons';

import './style.scss';

const ScheduleLineCard = ({
    episode,
    opened = false,
    hasIntervals = false,
    onClick,
    currentUser,
    checkAuth,
    onRateEpisode,
}) => {
    const { page, scheduleFilters, hasManyHalls, widgetSettings } = useScheduleContext();
    const { handleBookmark, bookmarkEpisodeIds } = useApplicationContext();
    const { event } = useEventContext();
    const { isWidget } = useWidgetContext();
    const $locale = useLocaleContext();
    const { eventKey } = useEventContext();
    const { renderModal } = useModalContext();
    const [isOpened, setOpened] = useState((isWidget ? widgetSettings?.showDescription : page.settings.showDescription) || opened);
    const ratedStorageKey = `${eventKey}/episodes/rated`;
    const ratedEpisodes = storage.get(ratedStorageKey) || {};
    const [isRated, setIsRated] = useState(ratedEpisodes[episode.id]?.includes(currentUser?.clientRegistrationId ?? currentUser?.id));

    const hasMembers = useMemo(() => {
        return !!episode.groups?.length;
    }, [episode]);

    const speakersFullConfig = useMemo(() => {
        return {
            visibility: 'visible',
            display: isOpened ? 'block' : 'none',
            opacity: isOpened ? 1 : 0,
            transform: `translateY(${isOpened ? 0 : -15}px)`,
            transitionEnd: {
                visibility: isOpened ? 'visible' : 'hidden',
                display: isOpened ? 'block' : 'none',
            }

        };
    }, [isOpened]);

    const speakersSimpleConfig = useMemo(() => {
        return {
            visibility: 'visible',
            display: !isOpened ? 'block' : 'none',
            opacity: !isOpened ? 1 : 0,
            transform: `translateX(${!isOpened ? 0 : 15}px)`,
            transitionEnd: {
                visibility: !isOpened ? 'visible' : 'hidden',
                display: !isOpened ? 'block' : 'none',
            }

        };
    }, [isOpened]);

    const speakersTransition = {
        ease: 'linear',
        duration: .3,
    };

    function handleToggleInfo(e) {
        e.stopPropagation();
        setOpened(!isOpened);
    }

    const handleRateToggle = async (e) => {
        e.stopPropagation();

        const isAuth = await checkAuth(currentUser);
        if (isAuth) {
            const value = !isRated;
            setIsRated(isRated => !isRated);
            const ratedEpisodes = storage.get(ratedStorageKey) || {};
            const arr = ratedEpisodes[episode.id] || [];
            const ratedStorageValue = { ...ratedEpisodes, [episode.id]: value ? [...arr, currentUser?.id] : arr.filter(id => id !== currentUser?.id) };
            onRateEpisode(ratedStorageKey, episode.id, value, ratedStorageValue);
        }
    };

    return (
        <div
            className={`schedule-card-line background-blur ${!hasMembers ? 'schedule-card-line--no-speakers' : ''} ${!isOpened ? 'schedule-card-line--closed' : ''} ${hasIntervals ? 'schedule-card-line--interval' : ''}`}
            style={{
                borderTop: (!!episode.stream?.color && !episode.hideStreamColor) ? `2px solid ${episode.stream.color}` : 'none',
                backgroundColor: episode.paintInStreamColor && !!episode.stream.color ? hexToRgba(episode.stream.color, 10) : '',
            }}
            onClick={onClick}
        >
            {
                (hasManyHalls && !episode.hideStreamDescription) &&
                <div className="schedule-card-line-hall">
                    <p>
                        {$locale.getProperty(episode.stream, 'description')}
                    </p>
                </div>
            }
            <div className="schedule-card-line-wrapper">
                <div className="schedule-card-line-header">
                    {
                        (episode.importantEpisode && scheduleFilters.scheduleView !== PAGES_VIEW_LINES) &&
                        <div className={`schedule-card-line-header__time ${(episode.importantEpisode && episode.displayTimeLarge) ? 'schedule-card-line-header__time--lg' : ''}`}>
                            {unixFormat(episode.timeStart, 'HH:mm')} — {unixFormat(episode.timeStop, 'HH:mm')}
                        </div>
                    }
                    {
                        (scheduleFilters.scheduleView === PAGES_VIEW_LINES) &&
                        <h2
                            className={`schedule-card-line-title ${(episode.importantEpisode && episode.displayNameLarge) ? 'schedule-card-line-title--lg' : ''}`}
                            style={{ margin: 0 }}
                        >
                            {$locale.getProperty(episode, 'name')}
                        </h2>
                    }
                    <div className="schedule-card-line-header__info">
                        <ScheduleEpisodeStatus
                            timeStart={episode.timeStart}
                            timeStop={episode.timeStop}
                            isVisible={
                                isWidget
                                    ? scheduleFilters.scheduleView === PAGES_VIEW_LINES ? !!widgetSettings?.showListStatus : !!widgetSettings?.showTileStatus
                                    : page?.settings?.showStatuses
                            }
                        />
                        {/* Скрыто т.к. пока не используется */}
                        {/* <ScheduleUserCategory /> */}
                        {/* <ScheduleFullness /> */}
                        <EpisodeRecordStatus
                            episode={episode}
                            displayRecord={widgetSettings?.displayEpisodeRecord ?? false}
                        >
                            <ScheduleHeaderActions
                                episode={episode}
                                isFormatVisible={
                                    isWidget
                                        ? scheduleFilters.scheduleView === PAGES_VIEW_LINES ? !!widgetSettings?.showListParticipationType : !!widgetSettings?.showTileParticipationType
                                        : (page?.settings?.showFormats ?? true)
                                }
                                isCalendarVisible={
                                    isWidget
                                        ? scheduleFilters.scheduleView === PAGES_VIEW_LINES ? !!widgetSettings?.listListAddToCalendar : !!widgetSettings?.showTileAddToCalendar
                                        : (page?.settings?.showAddToCalendar ?? true)
                                }
                                handleBookmark={handleBookmark}
                                bookmarkEpisodeIds={bookmarkEpisodeIds}
                                isBookmarkVisible={!isWidget && event?.moduleEpisode?.episodeFavorite}
                            />
                        </EpisodeRecordStatus>
                    </div>
                </div>
                {
                    (episode.importantEpisode && scheduleFilters.scheduleView !== PAGES_VIEW_LINES) &&
                    <h2
                        className={`schedule-card-line-title ${episode.displayNameLarge ? 'schedule-card-line-title--lg' : ''}`}
                        onClick={onClick}
                    >
                        {$locale.getProperty(episode, 'name')}
                    </h2>
                }
                {
                    !!episode.tags?.length &&
                    <CTags
                        tags={episode.tags}
                        className="schedule-card-line-tags"
                    />
                }
                {
                    !!episode.partners?.length &&
                    <motion.div
                        key="ScheduleCardLinePartners"
                        animate={speakersFullConfig}
                        initial={speakersFullConfig}
                        transition={speakersTransition}
                        className="schedule-card-line-partners"
                    >
                        <PartnersBaseView
                            partners={episode.partners}
                            size="sm"
                        />
                    </motion.div>
                }
                {
                    ($locale.getProperty(episode, 'info')) &&
                    <motion.div
                        key="ScheduleCardLineInfo"
                        animate={speakersFullConfig}
                        initial={speakersFullConfig}
                        transition={speakersTransition}
                        className="schedule-card-line-info"
                    >
                        {$locale.getProperty(episode, 'info')}
                    </motion.div>
                }
                <div
                    className="schedule-card-line-speakers"
                    style={
                        isWidget ? {
                            '--app-border-radius-photo': widgetSettings?.listPhotoRoundingSize,
                        } : {}
                    }
                >
                    <motion.div
                        key="ScheduleSpeakersFullInfo"
                        animate={speakersFullConfig}
                        initial={speakersFullConfig}
                        transition={speakersTransition}
                    >
                        <ScheduleMemberFullInfo
                            episode={episode}
                            hasPhotos={
                                isWidget
                                    ? scheduleFilters.scheduleView === PAGES_VIEW_LINES ? !!widgetSettings?.showListMembersPhoto : !!widgetSettings?.showTileMembersPhoto
                                    : true
                            }
                            hasNames={
                                isWidget
                                    ? scheduleFilters.scheduleView === PAGES_VIEW_LINES ? !!widgetSettings?.showListMembersName : !!widgetSettings?.showTileMembersName
                                    : true
                            }
                            handleBookmark={handleBookmark}
                            bookmarkEpisodeIds={bookmarkEpisodeIds}
                            isBookmarkVisible={!isWidget && event?.moduleEpisode?.episodeFavorite}
                        />
                    </motion.div>
                    <motion.div
                        key="ScheduleSpeakersSimpleView"
                        animate={speakersSimpleConfig}
                        initial={speakersSimpleConfig}
                        transition={speakersTransition}
                    >
                        {/* <div className="schedule-card-line-speakers-short">
                            <MembersSimpleView
                                membersType={$locale.get('member.label.moderators')}
                                members={episodeMembers(episode.id, event.members)}
                            />
                        </div> */}
                        {
                            !!episode.groups?.length &&
                            <div className="schedule-card-line-speakers-short">
                                <MembersSimpleView
                                    episode={episode}
                                    hasPhotos={
                                        isWidget
                                            ? scheduleFilters.scheduleView === PAGES_VIEW_LINES ? !!widgetSettings?.showListMembersPhoto : !!widgetSettings?.showTileMembersPhoto
                                            : page?.settings?.showPhotos ?? true
                                    }
                                    hasNames={
                                        isWidget
                                            ? scheduleFilters.scheduleView === PAGES_VIEW_LINES ? !!widgetSettings?.showListMembersName : !!widgetSettings?.showTileMembersName
                                            : true
                                    }
                                    onMemberClick={(member) => {
                                        renderModal(
                                            MemberDetailsModal,
                                            { member, lockPageScroll: false, settings: isWidget ? widgetSettings : page?.settings ?? null },
                                            null,
                                            (isWidget ? widgetSettings?.showMemberPopup : true)
                                        )
                                    }}
                                />
                            </div>
                        }
                    </motion.div>
                </div>
                {
                    (hasMembers || !!$locale.getProperty(episode, 'info')) &&
                    <CButton
                        icon="arrow_down"
                        round
                        className={`schedule-card-line-toggle-view ${isOpened ? 'schedule-card-line-toggle-view--opened' : ''}`}
                        onClick={(e) => handleToggleInfo(e)}
                    />
                }
            </div>
            <div className="schedule-card-line-footer">
                {onRateEpisode && (
                    <a onClick={handleRateToggle} className={`schedule-card-line-like ${isRated ? '-active' : ''}`}>
                        <Icons name="like_message" active={isRated} />{episode.like}
                    </a>
                )}
            </div>
        </div>
    )
};

export default ScheduleLineCard;
